import React from "react";
import { TimezoneInfo, useTimezone } from "../../hooks/useTimezone";
import { format } from "date-fns";
import { tz } from "@date-fns/tz";

export type ZonedDateTimeProps = {
  value: string | number | Date | null | undefined;
  formatString?: string;
  timezone?: string;
  timeWithRespectToAgencyTimezone?: boolean;
  disableAutoTimezoneIndicator?: boolean;
};

export function formatZonedDateTime({
  value,
  formatString = "M/d/yyyy, h:mm a",
  timezone: timezoneProp,
  timeWithRespectToAgencyTimezone = true,
  disableAutoTimezoneIndicator = false,
  timezoneInfo,
}: ZonedDateTimeProps & { timezoneInfo: TimezoneInfo }) {
  if (!value) return null;

  const timezone = !!timezoneProp ? timezoneProp : timezoneInfo.defaultTimezoneInfo.timezone;

  const shouldHideTimezone =
    !timeWithRespectToAgencyTimezone || timezoneInfo.clientIsInAgencyTimezone || disableAutoTimezoneIndicator;

  const date = new Date(value);
  const formatted = format(date, formatString, { in: tz(timezone!) });
  if (shouldHideTimezone) return formatted;
  const tzAbbr = new Intl.DateTimeFormat("en-US", { timeZone: timezone!, timeZoneName: "short" })
    .formatToParts(date)
    .find((p) => p.type === "timeZoneName")?.value;
  return tzAbbr ? `${formatted} ${tzAbbr}` : formatted;
}

/**
 * @see {@link https://date-fns.org/v4.1.0/docs/format} for format string options
 */
export function useZonedDateTime(props: ZonedDateTimeProps): string | null {
  const timezoneInfo = useTimezone({ defaultToAgencyTimezone: props.timeWithRespectToAgencyTimezone });
  return formatZonedDateTime({
    ...props,
    timezoneInfo,
  });
}

/**
 * @see {@link https://date-fns.org/v4.1.0/docs/format} for format string options
 */
export function ZonedDateTime(props: ZonedDateTimeProps) {
  const formattedDate = useZonedDateTime(props);
  if (!props.value) return null;
  return <time dateTime={new Date(props.value).toISOString()}>{formattedDate}</time>;
}
